/**
 * The Add a provider grid (slice 1c spec §6.1, §6.2): a two-column grid of
 * tiles, each with the provider's mark, its name and how it connects.
 * `/setup` picks one (a radio group, since the form follows below it).
 * Settings' Add a provider (`settings/add-provider.tsx`) lists companies instead.
 */
import { RadioGroup, RadioGroupCard } from "@crew/ui/components/radio-group";
import { cn } from "@crew/ui/lib/utils";
import type { AiProvider } from "../../app/me";
import { PROVIDERS } from "./onboarding";
import { PROVIDER_MARKS } from "./provider-marks";

/**
 * A provider's mark: its logo (`provider-marks.ts`) in its brand colour on
 * white, framed like an integration's app icon; `small` is the model
 * picker's 22px. Decorative: the name is beside it.
 */
export function ProviderLogo({
  provider,
  small = false,
  className,
}: {
  provider: AiProvider;
  small?: boolean;
  className?: string;
}) {
  const mark = PROVIDER_MARKS[provider];
  return (
    <span
      aria-hidden
      data-provider-logo={provider}
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-md bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.1)]",
        small && "size-5.5 rounded-[6px]",
        className,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        focusable="false"
        fill={mark.tone}
        className={small ? "size-3.25" : "size-4"}
      >
        <path d={mark.path} fillRule={mark.evenOdd ? "evenodd" : undefined} />
      </svg>
    </span>
  );
}

const tile =
  "flex w-full items-center gap-3 rounded-lg px-3.5 py-3 text-left ring-1 ring-input transition-colors hover:bg-foreground/3 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

function TileText({ provider }: { provider: AiProvider }) {
  return (
    <span className="flex min-w-0 grow flex-col gap-px">
      <span className="text-sm leading-4.5 font-semibold text-foreground">
        {PROVIDERS[provider].name}
      </span>
      <span className="text-meta leading-4.5 text-muted-foreground">
        {PROVIDERS[provider].about}
      </span>
    </span>
  );
}

/**
 * `/setup`'s provider choice: the grid as a radio group. `locked` keeps the
 * choice while a ChatGPT sign-in waits: the others dim.
 */
export function ProviderRadioGrid({
  providers,
  value,
  onChange,
  locked = false,
}: {
  providers: readonly AiProvider[];
  value: AiProvider | null;
  onChange: (provider: AiProvider) => void;
  locked?: boolean;
}) {
  return (
    <RadioGroup
      aria-label="Provider"
      value={value}
      onValueChange={(v) => onChange(v as AiProvider)}
      className="grid grid-cols-1 gap-2.5 sm:grid-cols-2"
    >
      {providers.map((p) => (
        <RadioGroupCard
          key={p}
          value={p}
          data-provider-tile={p}
          disabled={locked && p !== value}
          className={cn(
            tile,
            "cursor-pointer data-checked:bg-foreground/3 data-checked:ring-[1.5px] data-checked:ring-foreground",
          )}
        >
          <ProviderLogo provider={p} />
          <TileText provider={p} />
        </RadioGroupCard>
      ))}
    </RadioGroup>
  );
}
